import * as React from 'react';
import { cn } from '../lib/utils.js';
import { useMeasuredElement } from './use-measured-element.js';

/**
 * StickyHorizontalScrollbar
 *
 * A custom-drawn horizontal scrollbar that stays pinned on screen while the
 * page (not the table) owns vertical scrolling.
 *
 * Why this exists: a wide table inside a page-level scroll container grows to
 * its full content height, so its NATIVE horizontal scrollbar is painted at the
 * bottom edge of that tall box — hundreds of pixels down the document, reachable
 * only after scrolling to the very end of the list, and there it collides with
 * any bottom-docked summary bar. Bounding the table's height instead would
 * introduce a second vertical scroll container, which the offer workspace
 * deliberately avoids (one page scroller, sticky tabs bar).
 *
 * So the native horizontal scrollbar is hidden on the target element (see
 * `hideNativeScrollbarClassName`) and this control replaces it: a sticky track
 * with a drawn thumb that reads and writes `scrollLeft` on the target. The thumb
 * is drawn with normal DOM/CSS, so it is always visible regardless of the OS
 * "show scroll bars" setting (macOS overlay scrollbars stay hidden until an
 * active scroll gesture, which makes a native-scrollbar-based proxy invisible).
 *
 * Renders nothing when the target does not overflow horizontally.
 */

const MIN_THUMB_PX = 24;

/**
 * Apply to the scrollable element whose native horizontal scrollbar this
 * component replaces, so only one horizontal scrollbar is ever visible.
 * Scrolling behavior itself is untouched — only the scrollbar chrome is hidden.
 */
export const hideNativeScrollbarClassName = 'ooh-hide-native-scrollbar';

interface Metrics {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

export interface ThumbGeometry {
  /** Whether the target scrolls horizontally at all. */
  overflows: boolean;
  /** Largest valid `scrollLeft` on the target. */
  maxScrollLeft: number;
  thumbWidth: number;
  /** Distance the thumb can travel along the track. */
  thumbTravel: number;
  thumbLeft: number;
}

/**
 * Map scroll metrics onto thumb geometry. Pure so the proportions and their
 * edge cases (no overflow, unmeasured track, single-position travel) can be
 * tested without a live scroll container.
 */
export function computeThumbGeometry(metrics: Metrics & { trackWidth: number }): ThumbGeometry {
  const { scrollLeft, scrollWidth, clientWidth, trackWidth } = metrics;
  const overflows = scrollWidth > clientWidth + 1;
  const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

  if (!overflows || trackWidth <= 0) {
    return { overflows, maxScrollLeft, thumbWidth: 0, thumbTravel: 0, thumbLeft: 0 };
  }

  const thumbWidth = Math.min(
    trackWidth,
    Math.max(MIN_THUMB_PX, Math.round((clientWidth / scrollWidth) * trackWidth)),
  );
  const thumbTravel = Math.max(0, trackWidth - thumbWidth);
  const ratio = maxScrollLeft > 0 ? Math.min(1, Math.max(0, scrollLeft / maxScrollLeft)) : 0;

  return {
    overflows,
    maxScrollLeft,
    thumbWidth,
    thumbTravel,
    thumbLeft: Math.round(ratio * thumbTravel),
  };
}

/** Confine a thumb offset to the track. Single source of truth for the bound. */
export function clampThumbLeft(thumbLeft: number, thumbTravel: number): number {
  return Math.min(Math.max(thumbLeft, 0), thumbTravel);
}

/** Inverse of {@link computeThumbGeometry}: thumb offset back to `scrollLeft`. */
export function thumbLeftToScrollLeft(
  thumbLeft: number,
  geometry: Pick<ThumbGeometry, 'thumbTravel' | 'maxScrollLeft'>,
): number {
  const { thumbTravel, maxScrollLeft } = geometry;
  if (thumbTravel <= 0) return 0;
  return (clampThumbLeft(thumbLeft, thumbTravel) / thumbTravel) * maxScrollLeft;
}

function readMetrics(el: HTMLElement | null): Metrics {
  if (!el) return { scrollLeft: 0, scrollWidth: 0, clientWidth: 0 };
  return { scrollLeft: el.scrollLeft, scrollWidth: el.scrollWidth, clientWidth: el.clientWidth };
}

function useTargetMetrics(target: HTMLElement | null): Metrics {
  const [metrics, setMetrics] = React.useState<Metrics>(() => readMetrics(target));

  React.useEffect(() => {
    if (!target) {
      setMetrics({ scrollLeft: 0, scrollWidth: 0, clientWidth: 0 });
      return;
    }
    const commit = () => {
      setMetrics((prev) => {
        const next = readMetrics(target);
        if (
          prev.scrollLeft === next.scrollLeft &&
          prev.scrollWidth === next.scrollWidth &&
          prev.clientWidth === next.clientWidth
        ) {
          return prev;
        }
        return next;
      });
    };

    // Scroll events fire many times per frame on a trackpad. Coalesce them into
    // one state update per animation frame, otherwise each event re-renders this
    // component and the thumb visibly trails the pointer.
    let frame = 0;
    const sync = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        commit();
      });
    };

    commit();
    target.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    // Column resize/reorder and row loading change scrollWidth without firing
    // scroll or window resize — observe the element and its content instead.
    const observer = new ResizeObserver(sync);
    observer.observe(target);
    const content = target.firstElementChild;
    if (content) observer.observe(content);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      target.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      observer.disconnect();
    };
  }, [target]);

  return metrics;
}

export interface StickyHorizontalScrollbarProps {
  /** The horizontally scrollable element this scrollbar drives. */
  target: HTMLElement | null;
  /**
   * Distance from the bottom of the scroll viewport, in px — use the height of
   * whatever is docked below (e.g. a totals footer) so the two never overlap.
   */
  bottomOffset?: number;
  className?: string;
  'data-testid'?: string;
  /** Accessible label for the scrollbar control. */
  ariaLabel: string;
}

export function StickyHorizontalScrollbar({
  target,
  bottomOffset = 0,
  className,
  ariaLabel,
  ...rest
}: StickyHorizontalScrollbarProps) {
  const metrics = useTargetMetrics(target);
  const { scrollLeft, clientWidth } = metrics;
  const [trackWidth, trackRef] = useMeasuredElement<HTMLDivElement>((el) => el.clientWidth);
  const trackElRef = React.useRef<HTMLDivElement | null>(null);

  const setTrackNode = React.useCallback(
    (node: HTMLDivElement | null) => {
      trackElRef.current = node;
      trackRef(node);
    },
    [trackRef],
  );

  const geometry = computeThumbGeometry({ ...metrics, trackWidth });
  const { overflows, maxScrollLeft, thumbWidth, thumbTravel, thumbLeft } = geometry;

  const scrollToThumbLeft = React.useCallback(
    (nextThumbLeft: number) => {
      if (!target || thumbTravel <= 0) return;
      target.scrollLeft = thumbLeftToScrollLeft(nextThumbLeft, { thumbTravel, maxScrollLeft });
    },
    [target, thumbTravel, maxScrollLeft],
  );

  const scrollByAmount = React.useCallback(
    (delta: number) => {
      if (!target) return;
      target.scrollLeft = Math.min(Math.max(target.scrollLeft + delta, 0), maxScrollLeft);
    },
    [target, maxScrollLeft],
  );

  // Hiding the native scrollbar removes the only affordance keyboard users had
  // for scrolling this table sideways, so the drawn one has to take over that
  // job: role="scrollbar" is expected to respond to arrow/Home/End keys.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = Math.max(40, Math.round(clientWidth * 0.15));
    const page = Math.max(step, clientWidth);
    const actions: Record<string, () => void> = {
      ArrowLeft: () => scrollByAmount(-step),
      ArrowRight: () => scrollByAmount(step),
      PageUp: () => scrollByAmount(-page),
      PageDown: () => scrollByAmount(page),
      Home: () => scrollToThumbLeft(0),
      End: () => scrollToThumbLeft(thumbTravel),
    };
    const action = actions[event.key];
    if (!action) return;
    event.preventDefault();
    action();
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startLeft = thumbLeft;
    const thumbEl = event.currentTarget;
    thumbEl.setPointerCapture(event.pointerId);

    // Drag runs outside React: `pointermove` can fire several times per frame,
    // and every `scrollLeft` write echoes back through the scroll listener as
    // another render. Coalesce moves into one write per frame and position the
    // thumb by direct style mutation, so dragging stays on the compositor and
    // React only catches up via the (already rAF-batched) scroll sync.
    let frame = 0;
    let pendingLeft = startLeft;

    const flush = () => {
      frame = 0;
      scrollToThumbLeft(pendingLeft);
      // Same clamp the scroll position was derived from, so the drawn thumb and
      // the actual scroll offset can never disagree at the track's ends.
      thumbEl.style.left = `${clampThumbLeft(pendingLeft, thumbTravel)}px`;
    };

    const handleMove = (moveEvent: PointerEvent) => {
      pendingLeft = startLeft + (moveEvent.clientX - startX);
      if (frame) return;
      frame = requestAnimationFrame(flush);
    };
    // `pointercancel` matters as much as `pointerup`: a touch drag interrupted
    // by an OS gesture or app switch never fires `pointerup`, which would leave
    // these listeners attached and still scrolling after the gesture ended.
    const handleEnd = (endEvent: PointerEvent) => {
      if (frame) {
        cancelAnimationFrame(frame);
        flush();
      }
      if (thumbEl.hasPointerCapture(endEvent.pointerId)) {
        thumbEl.releasePointerCapture(endEvent.pointerId);
      }
      thumbEl.removeEventListener('pointermove', handleMove);
      thumbEl.removeEventListener('pointerup', handleEnd);
      thumbEl.removeEventListener('pointercancel', handleEnd);
    };

    thumbEl.addEventListener('pointermove', handleMove);
    thumbEl.addEventListener('pointerup', handleEnd);
    thumbEl.addEventListener('pointercancel', handleEnd);
  };

  // Click anywhere on the track: center the thumb there, like a native track click.
  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const track = trackElRef.current;
    if (!track) return;
    const clickX = event.clientX - track.getBoundingClientRect().left;
    scrollToThumbLeft(clickX - thumbWidth / 2);
  };

  if (!overflows) return null;

  return (
    <div
      className={cn('sticky z-20 w-full px-0', className)}
      style={{ bottom: bottomOffset }}
      {...rest}
    >
      <div
        ref={setTrackNode}
        role="scrollbar"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={maxScrollLeft}
        aria-valuenow={Math.round(scrollLeft)}
        tabIndex={0}
        onPointerDown={handleTrackPointerDown}
        onKeyDown={handleKeyDown}
        className="relative h-3 cursor-pointer border-t border-border bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        <div
          onPointerDown={handleThumbPointerDown}
          className="absolute top-0.5 h-2 rounded-full bg-foreground/35 transition-colors hover:bg-foreground/55"
          style={{ width: thumbWidth, left: thumbLeft }}
        />
      </div>
    </div>
  );
}
